'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Floating "back to top" button with a live scroll-progress ring.
 *  - Hidden until the user scrolls past SHOW_AFTER; fades + scales in.
 *  - The ring around the icon fills as the page is scrolled (0 → 100%).
 *  - Smooth-scrolls on click; honors prefers-reduced-motion (instant scroll).
 *  - Scroll/resize listener is rAF-throttled so it never churns the main thread.
 *  - Safe-area aware so it clears the iOS home bar / notch.
 */
const SHOW_AFTER = 320;
const RING_RADIUS = 25;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export default function ScrollToTop() {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1 page scroll depth
  const ticking = useRef(false);

  useEffect(() => {
    const handle = () => {
      if (ticking.current) return;
      ticking.current = true;
      window.requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        const max =
          document.documentElement.scrollHeight - window.innerHeight;
        setVisible(scrollY > SHOW_AFTER);
        setProgress(max > 0 ? Math.min(1, Math.max(0, scrollY / max)) : 0);
        ticking.current = false;
      });
    };
    handle();
    window.addEventListener('scroll', handle, { passive: true });
    window.addEventListener('resize', handle, { passive: true });
    return () => {
      window.removeEventListener('scroll', handle);
      window.removeEventListener('resize', handle);
    };
  }, []);

  const handleClick = useCallback(() => {
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  }, []);

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Back to top"
      title="Back to top"
      tabIndex={visible ? 0 : -1}
      aria-hidden={!visible}
      className={`group fixed z-[39] inline-flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-blue-100 to-blue-80 text-constant-white outline-none shadow-[0_12px_32px_-10px_rgba(52,59,145,0.6),0_3px_8px_-3px_rgba(15,23,42,0.22)] transition-[opacity,transform,box-shadow] duration-300 ease-out hover:shadow-[0_18px_44px_-10px_rgba(52,59,145,0.72),0_5px_12px_-3px_rgba(15,23,42,0.3)] focus-visible:ring-2 focus-visible:ring-constant-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-100 active:scale-95 ${
        visible
          ? 'pointer-events-auto translate-y-0 scale-100 opacity-100'
          : 'pointer-events-none translate-y-3 scale-90 opacity-0'
      }`}
      style={{
        bottom: 'calc(1.25rem + env(safe-area-inset-bottom, 0px))',
        right: 'calc(1.25rem + env(safe-area-inset-right, 0px))',
      }}
    >
      {/* Scroll-progress ring (decorative): faint track + filling arc. */}
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full -rotate-90"
        viewBox="0 0 56 56"
        aria-hidden
      >
        <circle
          cx="28"
          cy="28"
          r={RING_RADIUS}
          fill="none"
          stroke="currentColor"
          strokeOpacity={0.22}
          strokeWidth="2.5"
        />
        <circle
          cx="28"
          cy="28"
          r={RING_RADIUS}
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={RING_CIRCUMFERENCE}
          strokeDashoffset={RING_CIRCUMFERENCE * (1 - progress)}
          className="transition-[stroke-dashoffset] duration-150 ease-out"
        />
      </svg>

      {/* Up chevron — gently nudges up on hover. */}
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden
        className="relative transition-transform duration-200 ease-out group-hover:-translate-y-0.5"
      >
        <path
          d="M6 14l6-6 6 6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}
